import {AiMatchedLine, ExtractedDocument, ExtractedProductLine, ProductMatchCandidate, TextToDataProcess} from './types';
import {getAiProviderKey} from '../../api/customer/ai_keys.api';
import {
    aiProductExtraction,
    aiProductMatching,
    aiDocument,
    aiDocumentExtract,
    aiDocumentResolve,
    aiDraftsCreate,
    aiDraftsSnapshot,
} from '../../api/customer/ai_assistent.api';
import type {
    AiTier,
    ExtractedDocumentSchema,
    DocProductLine,
} from '../../api/customer/ai_assistent.api';

type AiProvider = 'openai' | 'deepseek';

/** מנוע להתאמת מוצרים (שלב match). ערוך כאן — לא UI. */
export type TextToDataMatchBackend = 'openai' | 'deepseek_pro_thinking';

/** ברירת מחדל: GPT 5.4. כדי להפעיל DeepSeek Pro Thinking שנה ל־'deepseek_pro_thinking'. */
export const TEXT_TO_DATA_MATCH_BACKEND: TextToDataMatchBackend = 'openai';

/** TRUE = כל קריאות החילוץ/התאמה עוברות דרך ai_assistent (`/v1/services/product-extraction(-matching)`). */
export const ROUTE_VIA_AI_ASSISTENT = true;

/** מקסימום מועמדים לכל שורה אחרי איחוד (כדי לא לפוצץ tokens). */
const MAX_CANDIDATES_PER_LINE = 12;

interface ChatOptions {
    provider: AiProvider;
    model: string;
    messages: { role: 'system' | 'user'; content: string }[];
    temperature?: number;
    /** שדות נוספים ל-body (למשל thinking / reasoning_effort ב-DeepSeek). */
    extras?: Record<string, unknown>;
    /** כש-false — לא שולחים response_format (למשל ב־thinking). ברירת מחדל true. */
    jsonResponse?: boolean;
}

export interface TextToDataAiDebugEvent {
    stage: string;
    title: string;
    data: any;
}

type DebugLogger = (event: TextToDataAiDebugEvent) => void;

const OPENAI_URL = 'https://api.openai.com/v1/chat/completions';
const DEEPSEEK_URL = 'https://api.deepseek.com/chat/completions';

const getRuntimeValue = (key: string) => {
    const win = typeof window !== 'undefined' ? (window as any) : {};
    return win?.[key] || localStorage.getItem(key) || '';
};

const getApiKey = (provider: AiProvider) => getAiProviderKey(provider);

export const textToDataAiConfig = {
    extractProvider: (getRuntimeValue('TEXT_TO_DATA_EXTRACT_PROVIDER') as AiProvider) || 'deepseek',
    extractModel: getRuntimeValue('TEXT_TO_DATA_EXTRACT_MODEL') || 'deepseek-chat',
    /** בשימוש רק כש־TEXT_TO_DATA_MATCH_BACKEND === 'openai' */
    matchProvider: (getRuntimeValue('TEXT_TO_DATA_MATCH_PROVIDER') as AiProvider) || 'openai',
    matchModel: getRuntimeValue('TEXT_TO_DATA_MATCH_MODEL') || 'gpt-5.4',
};

export function extractJsonPayload(text: string): string {
    const trimmed = (text ?? '').trim();
    const fenced = trimmed.match(/```(?:json)?\s*([\s\S]*?)```/i);
    const candidate = fenced?.[1]?.trim() || trimmed;
    const arrayStart = candidate.indexOf('[');
    const objectStart = candidate.indexOf('{');
    const startCandidates = [arrayStart, objectStart].filter((index) => index >= 0);
    if (!startCandidates.length) throw new Error('AI response did not include JSON');
    const start = Math.min(...startCandidates);
    const endArray = candidate.lastIndexOf(']');
    const endObject = candidate.lastIndexOf('}');
    const end = Math.max(endArray, endObject);
    if (end < start) throw new Error('AI response JSON was incomplete');
    return candidate.slice(start, end + 1);
}

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

async function callChat(options: ChatOptions): Promise<string> {
    const token = await getApiKey(options.provider);
    if (!token) {
        throw new Error(
            options.provider === 'deepseek'
                ? 'Missing DeepSeek API key on Flask server. Configure DEEPSEEK_API_KEY.'
                : 'Missing OpenAI API key on Flask server. Configure OPENAI_API_KEY.'
        );
    }

    const body: Record<string, unknown> = {
        model: options.model,
        messages: options.messages,
    };
    if (options.temperature !== undefined) body.temperature = options.temperature;
    if (options.jsonResponse !== false) body.response_format = {type: 'json_object'};
    if (options.extras) Object.assign(body, options.extras);

    const url = options.provider === 'deepseek' ? DEEPSEEK_URL : OPENAI_URL;

    let lastError: any;
    for (let attempt = 0; attempt < 2; attempt += 1) {
        try {
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(body),
            });

            if (response.ok) {
                const data = await response.json();
                return data?.choices?.[0]?.message?.content ?? '';
            }

            const errorBody = await response.text().catch(() => '');
            // Retry only on 5xx / 429 once. 4xx errors (validation) — fail fast.
            if (response.status >= 500 || response.status === 429) {
                lastError = new Error(`AI request failed (${response.status}): ${errorBody}`);
                await sleep(700);
                continue;
            }
            throw new Error(`AI request failed (${response.status}): ${errorBody}`);
        } catch (networkError) {
            lastError = networkError;
            await sleep(700);
        }
    }
    throw lastError instanceof Error ? lastError : new Error('AI request failed');
}

const normalizeExtractedDocument = (payload: any): ExtractedDocument => {
    const rawProducts = Array.isArray(payload) ? payload : payload?.products;
    const products: ExtractedProductLine[] = (rawProducts ?? []).map((line: any, index: number) => ({
        lineId: String(line.lineId ?? line.line_id ?? `line_${index + 1}`),
        productName: String(line.productName ?? line.product_name ?? line.name ?? line.rawText ?? '').trim(),
        amount: Number(line.amount ?? line.quantity ?? line.qty ?? 0),
        unit: line.unit ?? null,
        price: line.price !== undefined && line.price !== null ? Number(line.price) : null,
        total: line.total !== undefined && line.total !== null ? Number(line.total) : null,
        barcode: line.barcode ?? null,
        extId: line.extId ?? line.externalId ?? line.external_id ?? null,
        confidence: line.confidence !== undefined ? Number(line.confidence) : undefined,
        rawText: line.rawText ?? line.raw_text ?? line.text,
    })).filter((line) => line.productName && line.amount > 0);

    return {
        date: payload?.date ?? null,
        entityName: payload?.entityName ?? payload?.clientName ?? payload?.supplierName ?? payload?.entity_name ?? null,
        entityId: payload?.entityId ?? payload?.clientId ?? payload?.supplierId ?? payload?.entity_id ?? null,
        total: payload?.total !== undefined && payload?.total !== null ? Number(payload.total) : null,
        products,
    };
};

/** Approx GPT/DeepSeek tokens: 1 token ≈ 4 chars (OpenAI rule of thumb). */
const estimateTokens = (value: unknown) => {
    const text = typeof value === 'string' ? value : JSON.stringify(value);
    return Math.ceil((text?.length ?? 0) / 4);
};

export async function extractDocumentFromText(input: {
    text: string;
    process: TextToDataProcess;
    knownUnits?: string[];
    debug?: DebugLogger;
}): Promise<ExtractedDocument> {
    if (ROUTE_VIA_AI_ASSISTENT) {
        const tier: AiTier = textToDataAiConfig.extractProvider === 'openai' ? 'heavy' : 'medium';
        input.debug?.({
            stage: 'extract',
            title: 'בקשת חילוץ ל-AI (דרך ai_assistent)',
            data: {tier, text: input.text, process: input.process, knownUnits: input.knownUnits},
        });
        const res = await aiProductExtraction({
            process: input.process,
            text: input.text,
            knownUnits: input.knownUnits ?? [],
            extractTier: tier,
            extractModel: textToDataAiConfig.extractProvider === 'deepseek' ? 'deepseek/deepseek-chat' : null,
            feature: 'text_to_data.extract',
            debug: true,
        });
        input.debug?.({stage: 'extract', title: 'תשובת ai_assistent', data: res});
        // server normalizes already; coerce types to ExtractedDocument shape
        return {
            date: res.extracted.date ?? null,
            entityName: res.extracted.entityName ?? null,
            entityId: res.extracted.entityId ?? null,
            total: res.extracted.total ?? null,
            products: res.extracted.products.map((p, i) => ({
                lineId: String(p.lineId ?? `line_${i + 1}`),
                productName: String(p.productName ?? '').trim(),
                amount: Number(p.amount ?? 0),
                unit: p.unit ?? null,
                price: p.price ?? null,
                total: p.total ?? null,
                barcode: p.barcode ?? null,
                extId: p.extId ?? null,
                confidence: p.confidence ?? undefined,
                rawText: p.rawText ?? undefined,
            })).filter((p) => p.productName && p.amount > 0),
        };
    }

    // ── Legacy direct path (kept for emergency fallback) ─────────────────────
    const unitsText = (input.knownUnits ?? []).filter(Boolean).join(', ');
    const system = [
        'You extract order or supplier invoice data for an Israeli ERP from free Hebrew text.',
        'Return JSON only with keys: date, entityName, entityId, total, products.',
        'products must be an array of {lineId, productName, amount, unit, price, total, confidence, rawText}.',
        'Use null for missing optional fields. confidence is 0..1.',
        'Do not match to ERP product ids in this step. Only extract what the source says.',
        '',
        '== Splitting the text into product entries ==',
        'The source can arrive in MANY shapes: new lines, commas, semicolons, tabs, slashes, multiple spaces, or several product/amount pairs concatenated together. Treat ALL of these as separators between product entries:',
        '  - line breaks (\\n, \\r)',
        '  - commas (",", "،"), semicolons, slashes',
        '  - the Hebrew connector "ו" / "וגם" / "גם" appearing between products',
        '  - any sequence "<name> <number>" repeated in the same physical line, e.g. "ארנב 4 לבנה 11" => 2 products.',
        'Strip noise/filler phrases that are not products: "אני רוצה", "תן לי", "צריך", "שלום", "תביא", "וגם אני רוצה", "וגם" — but keep the product mentions on either side of them.',
        'Whenever a separator OR a number OR a known unit word is encountered, SUSPECT a new product line is starting and try to extract it as such.',
        '',
        '== Amount detection ==',
        'Each product MUST have an amount. Detection rules in order of priority:',
        '  1) An explicit digit near the product name (before OR after) is the amount, e.g. "פיתות 14", "14 פיתות", "0.5 לחם".',
        '  2) Hebrew number words also count as amounts. Map them to digits:',
        '       "אחד"/"אחת"=1, "שניים"/"שתיים"=2, "שלוש"/"שלושה"=3, "ארבע"/"ארבעה"=4, "חמש"/"חמישה"=5,',
        '       "שש"/"שישה"=6, "שבע"/"שבעה"=7, "שמונה"=8, "תשע"/"תשעה"=9, "עשר"/"עשרה"=10,',
        '       "אחד עשר"=11, "שתים עשרה"=12, "עשרים"=20, "שלושים"=30, "חצי"=0.5, "רבע"=0.25.',
        '  3) If NO quantity (digit or number-word) is found near the product, default amount=1 (e.g. "בננה" => amount=1, "תפוז לבן" => amount=1).',
        'NEVER round, scale, or convert; copy the source value as-is.',
        '',
        '== Unit detection ==',
        'Known ERP units (recognize them even when slightly misspelled or abbreviated): ' + unitsText,
        'A unit may appear BEFORE the product name, AFTER the product name, or between the amount and the product name.',
        '  - "קרטון עגבניה" => productName="עגבניה", amount=1, unit="קרטון".',
        '  - "5 קרטון תפוח" => productName="תפוח", amount=5, unit="קרטון".',
        '  - "תפוח 5 קרטון" => productName="תפוח", amount=5, unit="קרטון".',
        '  - "לחם 5" => productName="לחם", amount=5, unit=null (no unit word in the text).',
        'Unit must be null unless the source explicitly contains a unit word for that product. NEVER invent ק"ג, יחידה, קרטון, ארגז on your own when the text does not say so.',
        '',
        '== Examples ==',
        'Input: "פיתה מרובעת 14\\nנבטים 3\\nארנב 4 לבנה 11\\nפיתות רגילות 15"',
        'Products: פיתה מרובעת=14, נבטים=3, ארנב=4, לבנה=11, פיתות רגילות=15.',
        '',
        'Input: "קרטון עגבניה\\n2 פיתות\\nלחם 5, בצל 9\\nמלפפון אחד\\nבננה\\nוגם אני רוצה תפוז לבן ו 15 תפוז שחור"',
        'Products:',
        '  עגבניה  amount=1   unit="קרטון"',
        '  פיתות   amount=2   unit=null',
        '  לחם     amount=5   unit=null',
        '  בצל     amount=9   unit=null',
        '  מלפפון  amount=1   unit=null',
        '  בננה    amount=1   unit=null',
        '  תפוז לבן  amount=1   unit=null',
        '  תפוז שחור amount=15  unit=null',
        '',
        'Output exactly one entry per detected product. Never invent products that are not in the input.',
    ].join('\n');

    const user = JSON.stringify({process: input.process, sourceText: input.text});

    const chatOptions: ChatOptions = {
        provider: textToDataAiConfig.extractProvider,
        model: textToDataAiConfig.extractModel,
        messages: [{role: 'system', content: system}, {role: 'user', content: user}],
        temperature: 0,
    };
    input.debug?.({
        stage: 'extract',
        title: 'בקשת חילוץ ל-AI',
        data: {
            ...chatOptions,
            estimatedInputTokens: estimateTokens(system) + estimateTokens(user),
        },
    });

    const content = await callChat(chatOptions);
    input.debug?.({stage: 'extract', title: 'תשובת RAW מחילוץ', data: content});

    const parsed = JSON.parse(extractJsonPayload(content));
    input.debug?.({stage: 'extract', title: 'JSON חילוץ אחרי parse', data: parsed});
    return normalizeExtractedDocument(parsed);
}

/**
 * Compact a candidate to a small payload — strip nulls, undefined, empty arrays.
 * Keep the shape the AI prompt promises but drop fields that don't add information.
 */
const compactCandidate = (candidate: ProductMatchCandidate) => {
    const out: Record<string, unknown> = {
        id: candidate.productId,
        name: candidate.name,
        score: candidate.score,
    };
    const availableUnits = (candidate.availableUnits ?? []).filter(Boolean);
    if (availableUnits.length) out.availableUnits = availableUnits;
    else if (candidate.unit) out.unit = candidate.unit;

    if (Number(candidate.priority ?? 0) > 0) out.priority = Number(candidate.priority);
    if (candidate.prevOrdered === 1) out.prevOrdered = 1;
    if (candidate.barcode) out.barcode = candidate.barcode;
    if (candidate.externalId) out.externalId = candidate.externalId;
    if (candidate.previousOrders?.length) {
        out.previousOrders = candidate.previousOrders.slice(0, 3).map((usage) => {
            const compact: Record<string, unknown> = {};
            if (usage.usedAmount !== undefined) compact.usedAmount = usage.usedAmount;
            if (usage.usedUnit) compact.usedUnit = usage.usedUnit;
            else if (usage.usedUnitIndex !== undefined) compact.usedUnitIndex = usage.usedUnitIndex;
            return compact;
        });
    }
    return out;
};

const compactExtractedLine = (line: ExtractedProductLine) => {
    const out: Record<string, unknown> = {
        lineId: line.lineId,
        productName: line.productName,
        amount: line.amount,
    };
    if (line.unit) out.unit = line.unit;
    if (line.price !== null && line.price !== undefined) out.price = line.price;
    return out;
};

type LegacySavedMatch = {
    req_name: string;
    req_unit?: string | null;
    match_id: number;
    match_name?: string;
    match_unit?: string | null;
};

const compactSavedMatches = (matches: LegacySavedMatch[] | undefined) => {
    if (!matches?.length) return [];
    return matches.slice(0, 30).map((memory) => {
        const out: Record<string, unknown> = {
            req_name: memory.req_name,
            match_id: memory.match_id,
            match_name: memory.match_name,
        };
        if (memory.req_unit) out.req_unit = memory.req_unit;
        if (memory.match_unit) out.match_unit = memory.match_unit;
        return out;
    });
};

export async function matchExtractedProducts(input: {
    process: TextToDataProcess;
    extracted: ExtractedDocument;
    candidatesByLine: Record<string, ProductMatchCandidate[]>;
    clientId?: string | number | null;
    supplierId?: string | number | null;
    previousProducts?: any[];
    conversionRateBlocker?: number;
    debug?: DebugLogger;
}): Promise<AiMatchedLine[]> {
    if (ROUTE_VIA_AI_ASSISTENT) {
        const useDeepseekProThinking = TEXT_TO_DATA_MATCH_BACKEND === 'deepseek_pro_thinking';
        const tier: AiTier = useDeepseekProThinking
            ? 'reasoning'
            : (textToDataAiConfig.matchProvider === 'openai' ? 'heavy' : 'medium');

        const candidatesByLine: Record<string, any[]> = {};
        Object.entries(input.candidatesByLine).forEach(([lineId, cands]) => {
            candidatesByLine[lineId] = cands.map((c) => ({
                productId: Number(c.productId),
                name: c.name,
                score: c.score,
                unit: c.unit,
                availableUnits: c.availableUnits,
                priority: c.priority,
                prevOrdered: c.prevOrdered,
                barcode: c.barcode,
                externalId: c.externalId,
                previousOrders: c.previousOrders?.slice(0, 3).map((u) => ({
                    usedAmount: u.usedAmount,
                    usedUnit: u.usedUnit,
                    usedUnitIndex: u.usedUnitIndex,
                })),
            }));
        });

        input.debug?.({stage: 'match', title: 'בקשת התאמה ל-AI (דרך ai_assistent)', data: {tier, lines: input.extracted.products.length}});
        const res = await aiProductMatching({
            process: input.process,
            extractedProducts: input.extracted.products.map((p) => ({
                lineId: p.lineId,
                productName: p.productName,
                amount: p.amount,
                unit: p.unit ?? null,
                price: p.price ?? null,
                total: p.total ?? null,
                confidence: p.confidence ?? null,
                rawText: p.rawText ?? null,
            })),
            candidatesByLine,
            clientId: input.clientId,
            supplierId: input.supplierId,
            useMemory: true,
            conversionRateBlocker: input.conversionRateBlocker ?? 0,
            tier,
            feature: 'text_to_data.match',
            debug: true,
        });
        input.debug?.({stage: 'match', title: 'תשובת ai_assistent', data: res});
        return res.matches.map((m) => ({
            lineId: String(m.lineId),
            productId: m.productId === null
                ? null
                : (m.productId === undefined ? undefined : Number(m.productId)),
            unit: m.unit ?? null,
            amount: m.amount === null || m.amount === undefined ? null : Number(m.amount),
            price: m.price === null || m.price === undefined ? null : Number(m.price),
            confidence: Math.max(0, Math.min(1, Number(m.confidence ?? 0))),
            reason: m.reason ?? undefined,
        }));
    }

    // ── Legacy direct path (kept for emergency fallback) ─────────────────────
    const system = [
        'You match Hebrew order/invoice product lines to ERP products.',
        'Input candidate fields: {id, name, score, availableUnits|unit, priority?, prevOrdered?, barcode?, externalId?, previousOrders?:[{usedAmount,usedUnit?}]}.',
        'Return ONLY this JSON: {"matches":[{lineId, productId, unit, amount, price, confidence, reason}]}.',
        'productId in the response is the candidate.id you chose. It MUST come from candidatesByLine[lineId]. If no candidate fits — return productId:null and confidence < 0.5; do not invent ids.',
        'candidatesByLine is already pre-filtered (top fuzzy + priority>0 + ordered in last 3 docs of the same doc-type), unique per id.',
        'score = fuzzy 0..1. priority>0 means the entity uses this product (sum of recent purchases). prevOrdered=1 means it appears in the entity\'s last 3 docs of the same type. inEntityPricelist=1 means it\'s in the entity pricelist with priority>0. exactSource=barcode/extId means the source row carried a structured identifier — treat as near-certain. memoryHint (0.80–0.99) means the entity\'s own memory has a near-identical req_name pointing at this product — VERY STRONG. previousOrders gives the units/amounts they typically used.',
        'Disambiguation order when names look similar: exactSource > memoryHint > prevOrdered=1 > inEntityPricelist=1 > higher priority > higher score.',
        'HARD RULE: if a candidate has memoryHint and no other candidate has exactSource, pick the candidate with the highest memoryHint and use confidence ≥ memoryHint.',
        'savedMatches are previous USER-APPROVED mappings for THIS entity. Use as a strong soft hint, but still pick productId only from candidates.',
        'Unit rules:',
        '  1) If extracted.unit exists and appears in candidate.availableUnits — use it.',
        '  2) Else, if candidate.previousOrders has a usedUnit — use that exact usedUnit.',
        '  3) Else, if conversionRateBlocker is 1 or 2 and candidate has multiple availableUnits — return availableUnits[1].',
        '  4) Otherwise return availableUnits[0] (or candidate.unit).',
        'amount rule: copy extracted.amount as-is. NEVER round, scale, or convert; the user fixes amounts manually.',
        'price rule: prefer extracted.price; if missing leave null (UI fills from product).',
        'confidence calibration: 1.0 only on exact name/barcode match; 0.85–0.95 strong fuzzy + priority>0 or prevOrdered=1; 0.6–0.8 fuzzy with no extra signal; <0.5 ambiguous.',
        'reason: short Hebrew sentence explaining the choice. Mention prevOrdered/priority/usedUnit when they tipped the decision. Hebrew only.',
        'Return exactly one match per lineId in extractedProducts. Never add lines that were not in the input.',
    ].join('\n');

    const compactCandidatesByLine: Record<string, ReturnType<typeof compactCandidate>[]> = {};
    Object.entries(input.candidatesByLine).forEach(([lineId, candidates]) => {
        compactCandidatesByLine[lineId] = candidates
            .slice(0, MAX_CANDIDATES_PER_LINE)
            .map(compactCandidate);
    });

    const userPayload: Record<string, unknown> = {
        process: input.process,
        conversionRateBlocker: input.conversionRateBlocker ?? 0,
        extractedProducts: input.extracted.products.map(compactExtractedLine),
        candidatesByLine: compactCandidatesByLine,
    };
    // Legacy direct OpenAI path — NihulKav memory removed; Supabase memory is server-side only.

    const useDeepseekProThinking = TEXT_TO_DATA_MATCH_BACKEND === 'deepseek_pro_thinking';
    const deepseekMatchModel =
        getRuntimeValue('TEXT_TO_DATA_DEEPSEEK_MATCH_MODEL') || 'deepseek-v4-pro';
    const reasoningRaw = getRuntimeValue('TEXT_TO_DATA_DEEPSEEK_REASONING_EFFORT');
    const reasoningEffort: 'low' | 'medium' | 'high' =
        reasoningRaw === 'low' || reasoningRaw === 'medium' || reasoningRaw === 'high' ? reasoningRaw : 'high';

    const userContent = JSON.stringify(userPayload);

    const chatOptions: ChatOptions = useDeepseekProThinking
        ? {
              provider: 'deepseek',
              model: deepseekMatchModel,
              messages: [
                  {role: 'system', content: system},
                  {role: 'user', content: userContent},
              ],
              extras: {
                  thinking: {type: 'enabled'},
                  reasoning_effort: reasoningEffort,
              },
              // thinking models are picky about response_format / temperature — let them generate freely.
              jsonResponse: false,
          }
        : {
              provider: textToDataAiConfig.matchProvider,
              model: textToDataAiConfig.matchModel,
              messages: [
                  {role: 'system', content: system},
                  {role: 'user', content: userContent},
              ],
              temperature: 0,
          };

    input.debug?.({
        stage: 'match',
        title: 'Payload התאמה שנשלח ל-AI',
        data: {
            ...userPayload,
            _stats: {
                lines: Object.keys(compactCandidatesByLine).length,
                totalCandidates: Object.values(compactCandidatesByLine).reduce((sum, list) => sum + list.length, 0),
                estimatedSystemTokens: estimateTokens(system),
                estimatedUserTokens: estimateTokens(userContent),
                estimatedTotalTokens: estimateTokens(system) + estimateTokens(userContent),
            },
        },
    });
    input.debug?.({stage: 'match', title: 'בקשת התאמה ל-AI', data: chatOptions});

    const content = await callChat(chatOptions);
    input.debug?.({stage: 'match', title: 'תשובת RAW מהתאמה', data: content});

    const parsed = JSON.parse(extractJsonPayload(content));
    input.debug?.({stage: 'match', title: 'JSON התאמה אחרי parse', data: parsed});
    return (Array.isArray(parsed) ? parsed : parsed.matches ?? []).map((match: any) => ({
        lineId: String(match.lineId ?? match.line_id),
        // Distinguish "AI returned null on purpose" from "AI didn't return productId at all".
        // applyAiMatches relies on this difference to NOT auto-fallback to fuzzy when the AI explicitly said null.
        productId: match.productId === null
            ? null
            : match.productId === undefined
                ? undefined
                : Number(match.productId),
        unit: match.unit ?? null,
        amount: match.amount === undefined || match.amount === null ? null : Number(match.amount),
        price: match.price === undefined || match.price === null ? null : Number(match.price),
        confidence: Math.max(0, Math.min(1, Number(match.confidence ?? 0))),
        reason: match.reason,
    }));
}


// ═══════════════════════════════════════════════════════════════════════════
//  SMART pipeline (server-side extract + resolve, supports text/image/PDF)
// ───────────────────────────────────────────────────────────────────────────
//  Uses /v1/services/document-extract + /v1/services/document-resolve. The
//  server builds its own candidates from the ERP catalog (via MCP), checks
//  ai_match_memory and falls back to fuzzy+LLM — no local candidate building.
//
//  Returns the existing drawer-friendly shape so the rest of the UI doesn't
//  need to know which backend produced the data:
//     {extracted, matches, suggested: {entityId, entityName, date, allocation, docId}}
// ═══════════════════════════════════════════════════════════════════════════

/** Self-company identifier — tells the extractor "this party on the doc is US,
 *  pick the OTHER party as `entity`". Critical for PDFs that print both sides. */
export interface SmartSelfCompany {
    name?: string;
    tax_id?: string;
    aliases?: string[];
    id?: string;
}

export interface SmartExtractionInput {
    text?: string;
    image?: { base64: string; mime_type?: string };
    pdf?:   { base64: string };
    process: TextToDataProcess;
    /** When the UI already chose the entity, pass its ERP id so the server can skip
     *  entity matching and load scoped memory + previous orders. */
    entityId?: string | number | null;
    /** Optional name hint when no id (e.g. WhatsApp flow). */
    entityName?: string | null;
    /** Known units list (server prompt — recognizes units mid-Hebrew). */
    knownUnits?: string[];
    conversionRateBlocker?: number;
    /** When true, products flagged `available=0` are stripped from candidate
     *  lists and stale memory hits — matcher picks next-best AVAILABLE. */
    ignoreUnavailable?: boolean;
    /** Who *we* are on the document (helps the extractor pick the counterparty). */
    selfCompany?: SmartSelfCompany;
    /** Optional progress callback. When provided we split the smart pipeline
     *  into two HTTP calls (extract → resolve) and fire this between them so
     *  the UI can render the line table immediately while resolve is still
     *  running.  When omitted we keep the single-roundtrip /v1/services/document
     *  path (lower latency, no perceived progress). */
    onExtracted?: (partial: ExtractedDocument, schema: ExtractedDocumentSchema) => void | Promise<void>;
    debug?: DebugLogger;
    /**
     * When true (default) the analyze action also creates an `ai_assistent`
     * draft row in Supabase so the chat's DraftsDrawer can list it. The
     * legacy direct-create path in this drawer keeps working — this is a
     * pure-additive persistence hook.
     *
     * The callback fires once the draft is created (`onDraftCreated`) and
     * again with `onDraftReady` after the resolved snapshot is pushed. Both
     * are optional — the host can ignore them.
     */
    persistAsDraft?: boolean;
    onDraftCreated?: (draftId: string, publicNo: number) => void;
    onDraftReady?: (draftId: string) => void;
}

export interface SmartExtractionSuggested {
    entityId: string | number | null;
    entityName: string | null;
    externalEntityId: string | number | null;
    date: string | null;
    /** מספר הקצאה — Israeli allocation no. (`null` if not in source). */
    allocation: string | null;
    docId: string | null;
    canonicalDocType: string | null;
}

export interface SmartExtractionResult {
    extracted: ExtractedDocument;
    matches:   AiMatchedLine[];
    suggested: SmartExtractionSuggested;
    raw:       ExtractedDocumentSchema;
    /** Supabase draft id when `persistAsDraft` was true. The drawer can use
     *  this to deep-link from a "טעינה AI" tile or to call draftsApprove
     *  instead of the legacy direct-create path. */
    draftId?:  string | null;
}

/** Convert the unified document schema (fields with confidence) → the flat
 *  ExtractedDocument the drawer table already renders. */
function _smartToExtractedDoc(schema: ExtractedDocumentSchema): ExtractedDocument {
    const products: ExtractedProductLine[] = (schema.products ?? []).map((p, i) => ({
        lineId:     String(p.lineId ?? `line_${i + 1}`),
        productName: String(p.productName ?? '').trim(),
        amount:     Number(p.amount ?? 0),
        unit:       p.unit ?? null,
        price:      p.price ?? null,
        total:      p.total ?? null,
        barcode:    p.barcode ?? null,
        extId:      p.extId ?? null,
        confidence: p.confidence ?? undefined,
        rawText:    p.rawText ?? undefined,
    })).filter((p) => p.productName && p.amount > 0);

    return {
        date:       schema.date?.value ?? null,
        entityName: schema.entity?.name?.value ?? null,
        entityId:   schema.entity?.id != null ? String(schema.entity.id) : null,
        total:      schema.totals?.gross ?? schema.totals?.net ?? null,
        products,
    };
}

function _smartToAiMatches(schema: ExtractedDocumentSchema): AiMatchedLine[] {
    return (schema.products ?? []).map((p: DocProductLine) => ({
        lineId:     String(p.lineId),
        productId:  p.productId == null ? null : Number(p.productId),
        unit:       p.matchUnit ?? p.unit ?? null,
        amount:     p.resolvedAmount != null ? Number(p.resolvedAmount) : Number(p.amount ?? 0),
        price:      p.resolvedPrice  != null ? Number(p.resolvedPrice)  : (p.price ?? null),
        confidence: Math.max(0, Math.min(1, Number(p.matchConfidence ?? p.confidence ?? 0))),
        reason:     p.matchReason ?? undefined,
    }));
}

/** One-shot pipeline: text/image/PDF → extracted + resolved matches.
 *
 *  Calls the wrapper `/v1/services/document` endpoint (extract + resolve) and converts
 *  the unified schema back to the drawer's existing ExtractedDocument / AiMatchedLine
 *  shapes so the table renders without changes. `suggested` carries the extra
 *  fields the new pipeline returns (date, allocation, doc_id, …) for auto-fill.
 */
export async function runSmartDocumentExtraction(
    input: SmartExtractionInput,
): Promise<SmartExtractionResult> {
    const processToHint = (): 'order' | 'supplier_invoice' =>
        input.process === 'order' ? 'order' : 'supplier_invoice';
    const entityRole: 'client' | 'supplier' =
        input.process === 'order' ? 'client' : 'supplier';

    input.debug?.({
        stage: 'smart-extract',
        title: input.onExtracted
            ? 'בקשת document (extract → resolve, streaming UI)'
            : 'בקשת document (extract+resolve, single roundtrip)',
        data: {
            process: input.process,
            hasText:  Boolean(input.text),
            hasImage: Boolean(input.image),
            hasPdf:   Boolean(input.pdf),
            entityId: input.entityId,
            entityName: input.entityName,
            streaming: Boolean(input.onExtracted),
        },
    });

    // ── Draft persistence (parallel to the LLM call) ──────────────────────
    // The drafts API write is cheap and orthogonal to the analyze flow —
    // failures here are silent (we still want the user to see the analyze
    // result even if Supabase is temporarily down). We capture the id in
    // a closure so the resolve completion handler can push the final
    // snapshot back without coupling to React state.
    const persist = input.persistAsDraft !== false;
    let draftId: string | null = null;
    const draftPromise = persist
        ? (async () => {
            try {
                const file = input.image
                    ? {
                        filename:  input.image?.base64 ? `image-${Date.now()}.jpg` : 'image',
                        mime_type: input.image?.mime_type || 'image/jpeg',
                        base64:    input.image.base64,
                    }
                    : input.pdf
                        ? { filename: `doc-${Date.now()}.pdf`, mime_type: 'application/pdf', base64: input.pdf.base64 }
                        : null;
                const created = await aiDraftsCreate({
                    source: 'drawer',
                    process: processToHint(),
                    entity_role: entityRole,
                    hint_entity_id: input.entityId != null ? String(input.entityId) : null,
                    hint_entity_name: input.entityName ?? null,
                    text: input.text ?? null,
                    files: file ? [file] : [],
                    auto_run: false,
                });
                draftId = created.id;
                input.onDraftCreated?.(created.id, created.public_no);
                return created.id;
            } catch (err) {
                input.debug?.({
                    stage: 'draft-create',
                    title: 'יצירת טיוטה נכשלה (לא קריטי)',
                    data:  err,
                });
                return null;
            }
        })()
        : Promise.resolve(null as string | null);

    const hints = {
        process: processToHint(),
        entity_role: entityRole,
        known_entity: (input.entityId != null || input.entityName)
            ? {
                role: entityRole,
                id:   input.entityId != null ? String(input.entityId) : undefined,
                name: input.entityName ?? undefined,
            }
            : undefined,
        self_company: input.selfCompany
            ? {
                name:    input.selfCompany.name    ?? undefined,
                tax_id:  input.selfCompany.tax_id  ?? undefined,
                aliases: input.selfCompany.aliases ?? [],
                id:      input.selfCompany.id      ?? undefined,
            }
            : undefined,
        known_units: input.knownUnits ?? [],
    };
    const want = {
        match_doc_type: true,
        entity: {
            auto: input.entityId == null,
            force_id: input.entityId ?? null,
        },
        products: {
            auto: true,
            conversion_blocker: input.conversionRateBlocker ?? 0,
        },
    };
    const resolveOptions = {
        use_memory: true,
        match_tier: 'smart' as AiTier,
        entity_role: entityRole,
        process_override: processToHint(),
        ignore_unavailable: input.ignoreUnavailable ?? false,
        debug: true,
    };

    let resolveRes;
    if (input.onExtracted) {
        // ── Streaming UI path ───────────────────────────────────────────
        // 1) extract — usually 5-15s for PDF. Show lines immediately.
        // 2) resolve — usually 5-15s. Fill in matches when ready.
        const extractRes = await aiDocumentExtract({
            input:   { text: input.text, image: input.image, pdf: input.pdf },
            hints,
            options: { tier: 'smart', debug: true },
        });
        input.debug?.({
            stage: 'smart-extract-step1',
            title: 'תשובת extract (שלב 1/2)',
            data:  extractRes,
        });

        try {
            await input.onExtracted(_smartToExtractedDoc(extractRes.extracted), extractRes.extracted);
        } catch (cbErr) {
            input.debug?.({
                stage: 'smart-extract-step1-cb-error',
                title: 'onExtracted callback threw',
                data:  cbErr,
            });
        }

        // Persist the extract snapshot to the draft (non-blocking).
        void draftPromise.then((id) => {
            if (!id) return;
            aiDraftsSnapshot(id, {
                extracted: extractRes.extracted,
                usage: extractRes.usage,
            }).catch(() => { /* non-fatal */ });
        });

        resolveRes = await aiDocumentResolve({
            extracted: extractRes.extracted,
            want,
            options:   resolveOptions,
        });
        // Stitch the extract usage in front of the resolve usage so callers
        // see the full token/cost breakdown.
        resolveRes = {
            ...resolveRes,
            usage: [...(extractRes.usage ?? []), ...(resolveRes.usage ?? [])],
        };
        input.debug?.({
            stage: 'smart-resolve-step2',
            title: 'תשובת resolve (שלב 2/2)',
            data:  resolveRes,
        });
    } else {
        // ── Legacy single-roundtrip path ───────────────────────────────
        resolveRes = await aiDocument({
            input: { text: input.text, image: input.image, pdf: input.pdf },
            hints,
            extract_options: { tier: 'smart', debug: true },
            want,
            resolve_options: resolveOptions,
        });
        input.debug?.({stage: 'smart-resolve', title: 'תשובת document (extract+resolve)', data: resolveRes});
    }

    const schema = resolveRes.extracted;
    const docTypeBlock = schema.doc_type as
        | (NonNullable<ExtractedDocumentSchema['doc_type']>)
        | null;
    const canonicalDocType = docTypeBlock?.resolved?.canonical ?? docTypeBlock?.value ?? null;

    // Final snapshot — push the resolved data into the draft so it shows
    // up as `ready` in the chat's DraftsDrawer. Non-blocking: we already
    // have everything we need to return to the drawer.
    void draftPromise.then((id) => {
        if (!id) return;
        aiDraftsSnapshot(id, {
            extracted: schema,
            resolved:  schema,
            usage:     resolveRes.usage,
        })
            .then(() => input.onDraftReady?.(id))
            .catch(() => { /* non-fatal */ });
    });

    return {
        extracted: _smartToExtractedDoc(schema),
        matches:   _smartToAiMatches(schema),
        suggested: {
            entityId:   schema.entity?.id != null ? String(schema.entity.id) : null,
            entityName: schema.entity?.name?.value ?? null,
            externalEntityId: schema.entity?.external_id != null ? String(schema.entity.external_id) : null,
            date:       schema.date?.value ?? null,
            allocation: schema.allocation?.value ?? null,
            docId:      schema.doc_id?.value ?? null,
            canonicalDocType,
        },
        raw: schema,
        draftId,
    };
}
